import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

/** Shared Plant / chat scope for the active ingest session's changes. */
export type SessionPaintSelection =
  | { kind: 'all' }
  | { kind: 'section'; sectionId: string }
  /** Ingest rows written without a section id (job-level batches). */
  | { kind: 'jobLevel' }
  /** Branch fold restricted to agent-domain jobs. */
  | { kind: 'agent' }
  /** One contributing job's write log, ingest or agent. */
  | { kind: 'sourceJob'; jobId: string }

export interface SessionPaintScopeState {
  /** Branch the scope applies to; cleared when leaving a paintable branch. */
  branchId: string | undefined
  selection: SessionPaintSelection
  enabled: boolean
}

const initialState: SessionPaintScopeState = {
  branchId: undefined,
  selection: { kind: 'all' },
  enabled: true
}

const sessionPaintScopeSlice = createSlice({
  name: 'sessionPaintScope',
  initialState,
  reducers: {
    /**
     * Bind scope to the active paintable branch. Switching branches resets to
     * All; the first bind (undefined → id) keeps the current selection so chat
     * can pre-seed before Plant mounts.
     */
    bindSessionPaintBranch(state, action: PayloadAction<string | undefined>) {
      const nextBranchId = action.payload
      if (
        state.branchId !== undefined &&
        nextBranchId !== undefined &&
        state.branchId !== nextBranchId
      ) {
        state.selection = { kind: 'all' }
        state.enabled = true
      }
      state.branchId = nextBranchId
    },
    setSessionPaintSelection(state, action: PayloadAction<SessionPaintSelection>) {
      state.selection = action.payload
    },
    setSessionPaintEnabled(state, action: PayloadAction<boolean>) {
      state.enabled = action.payload
    }
  }
})

interface SessionPaintScopeRootState {
  sessionPaintScope: SessionPaintScopeState
}

export const { bindSessionPaintBranch, setSessionPaintSelection, setSessionPaintEnabled } =
  sessionPaintScopeSlice.actions

/** @deprecated Use bindSessionPaintBranch. */
export const bindSessionPaintJob = bindSessionPaintBranch

export function selectSessionPaintSelection(
  state: SessionPaintScopeRootState
): SessionPaintSelection {
  return state.sessionPaintScope.selection
}

export function selectSessionPaintEnabled(state: SessionPaintScopeRootState): boolean {
  return state.sessionPaintScope.enabled
}

export function selectSessionPaintScopeBranchId(
  state: SessionPaintScopeRootState
): string | undefined {
  return state.sessionPaintScope.branchId
}

/** @deprecated Use selectSessionPaintScopeBranchId. */
export function selectSessionPaintScopeJobId(
  state: SessionPaintScopeRootState
): string | undefined {
  return selectSessionPaintScopeBranchId(state)
}

export default sessionPaintScopeSlice.reducer
